import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBlocker, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AtiraEmptyWithAction } from '../../../components/AtiraEmptyWithAction';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { ImageCreateEdit } from '../../../components/ImageCreateEdit';
import { ImageUpload } from '../../../components/ImageUpload';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { WarningModal } from '../../../components/WarningModal';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { EditAtiraLinkDto } from '../../../model/atira-link/dto/EditAtiraLinkDto';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { AtiraLinkActions } from '../../../redux/atira-link/atira-link.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { getUserLinkUrl } from '../../../utils/AtiraLink';
import { AtiraToast } from '../../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';
import { LinkInput } from '../../my-link/components/LinkInput';
import { LinkTextarea } from '../../my-link/components/LinkTextarea';

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  width: 100%;
  align-items: flex-end;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const StyledSection = styled(Section)`
  width: 100%;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    min-width: 65%;

    .content-wrapper {
      gap: ${Spacing.m};
      flex-direction: row;
      align-items: center;
      height: 11rem;
    }
  }
`;

const ImageUploadWrapper = styled(Flex)`
  justify-content: center;

  .ant-upload-wrapper {
    border: ${({ theme }) => `1px solid ${theme.main}`} !important;
    border-radius: ${Rounded.circle} !important;
  }

  .ant-upload-select {
    border-radius: ${Rounded.circle} !important;
    border: 0 !important;
    overflow: hidden !important;
  }

  .ant-upload-list-picture-card {
    display: block !important;
    position: relative !important;
  }

  .ant-upload-list-item {
    border: 0 !important;
    padding: 0 !important;
    border-radius: ${Rounded.circle} !important;
    overflow: hidden !important;
  }

  .ant-upload-list-item-container {
    position: absolute !important;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }

  .ant-upload-list-item-actions {
    z-index: 5000 !important;
  }

  .ant-upload-list-item::before {
    width: 100% !important;
    height: 100% !important;
  }

  button {
    width: 100%;
    height: 100%;

    &:hover {
      background-color: ${({ theme }) => theme.lightMain} !important;
    }
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    justify-content: flex-start;
  }
`;

const PreviewWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  border-radius: ${Rounded.md};
  padding: ${Spacing.m};
  background-color: ${({ theme }) => theme.bgCanvas};
  width: 100%;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex;
    min-width: 34%;
    height: 11rem;
  }
`;

const PreviewInnerWrapper = styled(Flex)`
  border: ${({ theme }) => `1px solid ${theme.textColor}`};
  border-radius: ${Rounded.lg};
  width: 100%;
  overflow: hidden;
`;

const PreviewImageWrapper = styled(Flex)`
  width: 5rem;
  flex-shrink: 0;
  aspect-ratio: 1 / 1;

  svg {
    width: 70%;
    height: 70%;
    display: block;
    margin: auto;
    padding: ${Spacing.s};
  }

  img {
    height: 100% !important;
    object-fit: cover;
  }
`;

const PreviewContentWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding-inline: 0.3rem;
  overflow: hidden;
`;

const TextWithEllipsis = styled(Text)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const SettingsOpenGraph: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [nextLocationPath, setNextLocationPath] = useState('');

  const { t } = useTranslation();

  const {
    control,
    setValue,
    getValues,
    watch,
    handleSubmit,
    reset,
    setError,
    formState: { isDirty },
  } = useForm<EditAtiraLinkDto>();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const shouldBlock =
      currentLocation.pathname !== nextLocation.pathname && isDirty;
    if (shouldBlock) {
      blocker.reset?.();
      setNextLocationPath(nextLocation.pathname);
      setWarningModalVisible(true);
    }

    return shouldBlock;
  });

  const onConfirmWarning = () => {
    blocker.proceed?.();
    setWarningModalVisible(false);
    navigate(nextLocationPath);
  };

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const selectedAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const userAtiraLinks = useAppSelector(
    atiraLinkSliceSelectors.selectUserAtiraLinks,
  );

  const [title, description, image] = watch([
    'settings.openGraph.title',
    'settings.openGraph.description',
    'ogImage',
  ]);

  const onUpdateOpenGraph = async () => {
    try {
      setLoading(true);

      const payload = getValues();
      const finalPayload: EditAtiraLinkDto = {
        atiraLinkId: payload.atiraLinkId,
        userId,
        settings: omitBy(payload.settings || {}, isNil) as any,
      };

      if (
        (payload.ogImage as any) instanceof File ||
        payload.ogImage === null
      ) {
        finalPayload.ogImage = payload.ogImage;
      }

      await dispatch(
        AtiraLinkActions.editAtiraLink(finalPayload as EditAtiraLinkDto),
      ).unwrap();
      await dispatch(AtiraLinkActions.getUserAtiraLinks({ userId })).unwrap();

      AtiraToast.success(t('settings.open_graph.update.success'));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    reset({
      atiraLinkId: selectedAtiraLink?._id,
      settings: {
        openGraph: {
          title: selectedAtiraLink?.settings?.openGraph?.title || '',
          description:
            selectedAtiraLink?.settings?.openGraph?.description || '',
        },
      },
      ogImage: selectedAtiraLink?.settings?.openGraph?.ogImage || '',
      userId,
    });
  }, [reset, selectedAtiraLink, userId]);

  if (!userAtiraLinks.length) {
    return (
      <AtiraEmptyWithAction
        description={t('settings.no_link')}
        buttonTitle={t('common.create_link')}
        onClick={() => navigate('/my-link')}
      />
    );
  }

  return (
    <Container>
      <StyledSection title={t('settings.open_graph.title')}>
        <Controller
          control={control}
          name="ogImage"
          render={({ field: { onChange } }) => (
            <ImageUploadWrapper>
              <ImageUpload
                maxCount={1}
                onRemove={() => setValue('ogImage', '')}
                onChange={onChange}
                image={image || ''}
                dataTestId={atiraLinkTestIDs.settings.openGraph.image}
              />
            </ImageUploadWrapper>
          )}
        />

        <Flex flexDirection="column" gap="m" width={'100%'}>
          <Controller
            control={control}
            name="settings.openGraph.title"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <LinkInput
                title={t('common.title')}
                value={value}
                onChange={onChange}
                placeholder={t('common.title')}
                dataTestId={atiraLinkTestIDs.settings.openGraph.title}
                valid={!error}
                errorMessage={error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="settings.openGraph.description"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <LinkTextarea
                title={t('common.description')}
                value={value}
                onChange={onChange}
                placeholder={t('common.description')}
                dataTestId={atiraLinkTestIDs.settings.openGraph.description}
                valid={!error}
                errorMessage={error?.message}
              />
            )}
          />
        </Flex>
      </StyledSection>
      <PreviewWrapper>
        <PreviewInnerWrapper>
          <PreviewImageWrapper>
            <ImageCreateEdit src={image} style={{ zIndex: 1 }} />
          </PreviewImageWrapper>

          <PreviewContentWrapper>
            <TextWithEllipsis fontWeight={'bold'}>
              {title || t('common.title')}
            </TextWithEllipsis>

            <TextWithEllipsis color="fgSecondary">
              {description || t('common.description')}
            </TextWithEllipsis>

            <TextWithEllipsis fontSize="s" color="fgMuted">
              {getUserLinkUrl(selectedAtiraLink?.linkName!)}
            </TextWithEllipsis>
          </PreviewContentWrapper>
        </PreviewInnerWrapper>

        <Button
          loading={loading}
          onClick={handleSubmit(onUpdateOpenGraph)}
          hover
          hoverBackgroundColor="lightMain"
          height="2.5rem"
          dataTestId={atiraLinkTestIDs.settings.openGraph.submit}
        >
          {t('common.save')}
        </Button>
      </PreviewWrapper>

      <WarningModal
        title={t('atiraLink.formDirty.title')}
        description={t('atiraLink.formDirty.description')}
        open={warningModalVisible}
        onClose={() => setWarningModalVisible(false)}
        onConfirm={onConfirmWarning}
      />
    </Container>
  );
};
